import type { MapMode } from "@/features/map/contexts/MapControlContext";

interface DispatchAddressSearchParams {
  /** 検索対象の住居表示文字列（URL パラメータ由来）。 */
  address: string;
  /** chibanchizu・契約状態から事前算出した予想 mapMode。 */
  predictedMapMode: MapMode;
  /**
   * 地番検索に使えるデータソースが 1 つでもあるか（UI の検索ボタンの活性条件と同じ）。
   * 地図契約・契約不要の役所系ソース（役所地番地図・登記所備付地図）のどちらでもよい。
   */
  canUseParcelSearch: boolean;
  /** 地番検索が使えるときに呼ぶ住居表示→地番の逆引き検索ハンドラ。 */
  reverseSearch: (address: string, predictedMapMode: MapMode) => Promise<void>;
  /** 地番検索が使えないときに呼ぶ地番なしの地図移動検索ハンドラ。 */
  reverseSearchFree: (address: string) => Promise<void>;
}

/**
 * URL パラメータ経由の住居表示自動検索のディスパッチ（純粋関数）。
 *
 * 住居表示テキストボックス横の「検索」ボタンを押したのと等価の分岐を行う:
 *   - 地番検索に使えるデータソースがある: 住居表示→地番の逆引き検索
 *   - 無い: 地番なしで地図移動のみ（地番系パラメータ `chibanchizu=true` 付きの
 *     リンクで共有されても、使えるソースがなければ地番は表示されず無言で無料検索に
 *     降格する。エラーバナー・トースト等の通知は出さない仕様）。
 *
 * trim 後に空文字となる address は何も実行しない。
 *
 * ChibanViewer の `triggerAddressSearch` から検索可否と検索ハンドラを
 * 注入して利用する。分岐ルールを純粋関数として切り出すことでテスト可能にする。
 */
export const dispatchAddressSearch = async ({
  address,
  predictedMapMode,
  canUseParcelSearch,
  reverseSearch,
  reverseSearchFree,
}: DispatchAddressSearchParams): Promise<void> => {
  if (address.trim() === "") return;

  if (canUseParcelSearch) {
    await reverseSearch(address, predictedMapMode);
  } else {
    await reverseSearchFree(address);
  }
};
